/**
 * Route table (WP-P1). Every page is lazy-loaded; authenticated routes render
 * inside AppShell. Legacy routes redirect per BLUEPRINT §2.7 and the old
 * pages remain mounted under /legacy/* while `features.legacy_*` is on.
 */
import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from './stores/authStore'
import { hasMinimumRole, landingPathForRole } from './utils/roles'
import { LEGACY_REDIRECTS } from './navigation'
import { useFeatures } from './hooks/useFeatures'
import { getOnboardingStatus } from './services/api/onboarding'
import ErrorBoundary from './components/ErrorBoundary'
import AppShell from './shell/AppShell'
import { PageSkeleton } from './components/ui/Skeleton'

// Public
const Login = lazy(() => import('./pages/Login'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const PublicTracking = lazy(() => import('./pages/PublicTracking'))
const SignupPage = lazy(() => import('./features/signup/SignupPage'))
// WP-P6: email verification landing (BLUEPRINT §5).
const VerifyEmailPage = lazy(() => import('./features/signup/VerifyEmailPage'))

// Workspaces
const TodayPage = lazy(() => import('./features/today/TodayPage'))
const LoadsPage = lazy(() => import('./features/loads/LoadsPage'))
const DriversPage = lazy(() => import('./features/drivers/DriversPage'))
const PerformancePage = lazy(() => import('./features/performance/PerformancePage'))
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'))
const PlatformPage = lazy(() => import('./features/platform/PlatformPage'))
const MobileAppPage = lazy(() => import('./features/mobile/MobileAppPage'))
const Profile = lazy(() => import('./pages/Profile'))
const DriverDetail = lazy(() => import('./pages/DriverDetail'))
const ShiftReplay = lazy(() => import('./pages/ShiftReplay'))
const Admin = lazy(() => import('./pages/Admin'))

// Legacy (deprecated, feature-flagged)
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Assignments = lazy(() => import('./pages/Assignments'))
const Cascades = lazy(() => import('./pages/Cascades'))
const DutySchedule = lazy(() => import('./pages/DutySchedule'))

const fallback = <PageSkeleton />

function Page({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  )
}

/**
 * Layout route for every authenticated page: auth gate + AppShell mounted
 * exactly once (so the WebSocket, notification polling and palette survive
 * page changes). Per-page role checks live in <Require>.
 */
function AuthenticatedLayout() {
  const { isAuthenticated, role } = useAuthStore()
  const location = useLocation()
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }
  // DRIVER accounts have no portal surface — send them to the mobile-app page
  // instead of the old redirect loop (audit §3.4).
  if (role === 'DRIVER' && location.pathname !== '/mobile-app' && location.pathname !== '/profile') {
    return <Navigate to="/mobile-app" replace />
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}

function Require({ min, children }: { min?: string; children: ReactNode }) {
  const role = useAuthStore(s => s.role)
  if (min && role && !hasMinimumRole(role, min)) {
    return <Navigate to={landingPathForRole(role)} replace />
  }
  return <Page>{children}</Page>
}

/** `/` — role landing (BLUEPRINT §2 / audit §9). */
function RoleLanding() {
  const { isAuthenticated, role } = useAuthStore()
  const needsOnboarding = isAuthenticated && role === 'ADMIN'
  const { data, isLoading } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: getOnboardingStatus,
    enabled: needsOnboarding,
    staleTime: 60_000,
    retry: false,
  })
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (needsOnboarding && isLoading) return fallback
  // No onboarding service yet (empty checklist) → treat as complete so an
  // established account is not trapped on the setup screen.
  const progress = data && data.steps.length > 0 ? data.progress_pct : 100
  return <Navigate to={landingPathForRole(role, { onboardingProgress: progress })} replace />
}

/** Legacy path → new path, merging the new route's query with the old one. */
function LegacyRedirect({ to }: { to: string }) {
  const [params] = useSearchParams()
  const [path, query] = to.split('?')
  const merged = new URLSearchParams(query ?? '')
  params.forEach((v, k) => { if (!merged.has(k)) merged.set(k, v) })
  const qs = merged.toString()
  return <Navigate to={qs ? `${path}?${qs}` : path} replace />
}

/** Mounts a deprecated page only while its feature flag is on. */
function LegacyGate({ flag, fallbackTo, children }: { flag: string; fallbackTo: string; children: ReactNode }) {
  const { isEnabled, isLoading } = useFeatures()
  if (isLoading) return fallback
  if (!isEnabled(flag)) return <LegacyRedirect to={fallbackTo} />
  return <>{children}</>
}

export default function App() {
  const { isAuthenticated } = useAuthStore()

  return (
    <BrowserRouter>
      <Routes>
        {/* ── Public ─────────────────────────────────────────────────── */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Page><Login /></Page>} />
        <Route path="/signup" element={isAuthenticated ? <Navigate to="/" replace /> : <Page><SignupPage /></Page>} />
        <Route path="/forgot-password" element={<Page><ForgotPassword /></Page>} />
        <Route path="/reset-password" element={<Page><ResetPassword /></Page>} />
        <Route path="/verify-email" element={<Page><VerifyEmailPage /></Page>} />
        <Route path="/track/:token" element={<Page><PublicTracking /></Page>} />

        {/* ── Authenticated (single AppShell mount) ───────────────────── */}
        <Route element={<AuthenticatedLayout />}>
        {/* ── Workspaces ─────────────────────────────────────────────── */}
        <Route path="/today" element={<Require min="DISPATCHER"><TodayPage /></Require>} />
        <Route path="/loads" element={<Require min="DISPATCHER"><LoadsPage /></Require>} />
        <Route path="/loads/:id" element={<Require min="DISPATCHER"><LoadsPage /></Require>} />
        <Route path="/drivers" element={<Require min="DISPATCHER"><DriversPage /></Require>} />
        {/* More specific driver routes first so they keep resolving (audit §3.1). */}
        <Route path="/drivers/:loadId/replay" element={<Require min="DISPATCHER"><DriverDetail /></Require>} />
        <Route path="/drivers/:driverId/shift-summary" element={<Require min="DISPATCHER"><ShiftReplay /></Require>} />
        <Route path="/drivers/:id" element={<Require min="DISPATCHER"><DriversPage /></Require>} />
        <Route path="/performance" element={<Require min="DISPATCHER"><PerformancePage /></Require>} />
        <Route path="/settings/*" element={<Require min="DISPATCHER"><SettingsPage /></Require>} />
        <Route path="/platform/*" element={<Require min="SUPERADMIN"><PlatformPage /></Require>} />
        <Route path="/profile" element={<Require><Profile /></Require>} />
        <Route path="/mobile-app" element={<Require><MobileAppPage /></Require>} />

        {/* ── Legacy redirects (BLUEPRINT §2.7) ───────────────────────── */}
        <Route path="/dashboard" element={<LegacyRedirect to={LEGACY_REDIRECTS['/dashboard']} />} />
        <Route path="/assignments" element={<LegacyRedirect to={LEGACY_REDIRECTS['/assignments']} />} />
        <Route path="/cascades" element={<LegacyRedirect to={LEGACY_REDIRECTS['/cascades']} />} />
        <Route path="/duty-schedule" element={<LegacyRedirect to={LEGACY_REDIRECTS['/duty-schedule']} />} />
        <Route path="/pulse" element={<LegacyRedirect to={LEGACY_REDIRECTS['/pulse']} />} />
        <Route path="/reports" element={<LegacyRedirect to={LEGACY_REDIRECTS['/reports']} />} />
        <Route path="/superadmin/*" element={<LegacyRedirect to={LEGACY_REDIRECTS['/superadmin']} />} />
        <Route path="/admin/*" element={<Require min="DISPATCHER"><Admin /></Require>} />

        {/* ── Legacy pages, mounted while features.legacy_* is on ─────── */}
        <Route path="/legacy/dashboard" element={
          <Require min="DISPATCHER">
            <LegacyGate flag="legacy_dashboard" fallbackTo="/today?view=map"><div className="h-screen"><Dashboard /></div></LegacyGate>
          </Require>
        } />
        <Route path="/legacy/assignments" element={
          <Require min="DISPATCHER">
            <LegacyGate flag="legacy_assignments_page" fallbackTo="/loads?view=needs-driver"><Assignments /></LegacyGate>
          </Require>
        } />
        <Route path="/legacy/cascades" element={
          <Require min="DISPATCHER">
            <LegacyGate flag="legacy_cascades_page" fallbackTo="/today?tab=exceptions&type=KNOCK_ON"><Cascades /></LegacyGate>
          </Require>
        } />
        <Route path="/legacy/duty-schedule" element={
          <Require min="DISPATCHER">
            <LegacyGate flag="legacy_duty_schedule" fallbackTo="/drivers?view=board"><DutySchedule /></LegacyGate>
          </Require>
        } />

        </Route>

        {/* ── Landing + 404 ───────────────────────────────────────────── */}
        <Route path="/" element={<RoleLanding />} />
        <Route path="*" element={<RoleLanding />} />
      </Routes>
    </BrowserRouter>
  )
}
